import type { RevenueTier } from '@busy/api';
import { useT } from '../../lib/i18n';

// Top-revenue badge shown on a client profile + Top Clients list.
//
// Tiers are computed in the client_stats view (0050) using percent_rank
// over total_revenue_cents among clients with 3+ visits:
//   top1   — top 1% by revenue (>=0.99 percentile)
//   top10  — top 10% (>=0.90)
//   top25  — top 25% (>=0.75)
//
// Visual hierarchy: gold for top1 (rarest), navy for top10, slate for
// top25. Always uppercase + tracked, and we apply the matching paddingLeft
// fix so the visible text stays centered inside the pill.

const TIER_STYLE: Record<RevenueTier, { bg: string; text: string }> = {
  top1:  { bg: 'bg-gold-500',         text: 'text-navy-900' },
  top10: { bg: 'bg-navy-700/90',      text: 'text-gold-300' },
  top25: { bg: 'bg-emerald-700/85',   text: 'text-emerald-50' },
};

export function RevenueTierBadge({
  tier,
  className = '',
}: {
  tier: RevenueTier;
  className?: string;
}) {
  const t = useT();
  const style = TIER_STYLE[tier];
  return (
    <span
      className={`inline-flex items-center gap-1 py-0.5 pr-2 rounded-full text-[10px] font-extrabold tracking-[0.1em] uppercase ${style.bg} ${style.text} ${className}`}
      style={{ paddingLeft: 'calc(0.5rem + 0.1em)' }}
    >
      <CrownIcon className="w-2.5 h-2.5" />
      {t(`revenueTier.${tier}`)}
    </span>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M5 16l-2-9 5 4 4-7 4 7 5-4-2 9H5zm0 2h14v2H5z" />
    </svg>
  );
}
